import React from 'react';
import { Badge } from './ui/badge';
import { Shield, LifeBuoy, Plus, Clock } from 'lucide-react';

/**
 * AssignmentBadge (Iter-133w)
 *
 * Single source of truth for rendering a talent's assignment type on a
 * shift roster. Use this EVERYWHERE a booking row is displayed — admin
 * shift detail, admin shift list, client shift detail, calendar, PDFs,
 * anywhere. Never hand-roll "Primary" / "Backup" text again.
 *
 *   <AssignmentBadge type={booking.assignment_type} />
 *
 * The type is stored on bookings.assignment_type and is always one of:
 *   primary | backup | extra  (null is treated as primary for safety).
 *
 * Style matches our user-approved palette: 🟢 Primary (emerald),
 * 🟡 Backup (amber), 🔵 Extra (slate).
 *
 * Size prop ('sm' | 'md') controls padding + text size. `compact`
 * swaps "Primary Talent" / "Backup Talent" → "Primary" / "Backup".
 */
const ASSIGNMENT_MAP = {
  primary: {
    label: 'Primary Talent',
    short: 'Primary',
    cls: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    Icon: Shield,
  },
  backup: {
    label: 'Backup Talent',
    short: 'Backup',
    cls: 'bg-amber-100 text-amber-800 border-amber-300',
    Icon: LifeBuoy,
  },
  alternate: {
    // Iter-345 — pre-committed replacement, activated ad-hoc when a
    // primary drops out. Distinct from Backup (which typically reports
    // onsite for standby pay). Uses a violet accent so admins spot the
    // "call-if-needed" pool at a glance.
    label: 'Alternate Talent',
    short: 'Alternate',
    cls: 'bg-violet-100 text-violet-800 border-violet-300',
    Icon: Clock,
  },
  extra: {
    label: 'Extra',
    short: 'Extra',
    cls: 'bg-slate-100 text-slate-700 border-slate-300',
    Icon: Plus,
  },
};

export const AssignmentBadge = ({ type, size = 'sm', compact = false, className = '' }) => {
  const key = (type || 'primary').toLowerCase();
  const cfg = ASSIGNMENT_MAP[key] || ASSIGNMENT_MAP.primary;
  const I = cfg.Icon;
  const sizeCls = size === 'md' ? 'text-xs px-2 py-0.5' : 'text-[10px] px-1.5 py-0.5';
  return (
    <Badge
      className={`${cfg.cls} border font-medium uppercase tracking-wide ${sizeCls} ${className}`}
      data-testid={`assignment-badge-${key}`}
      title={cfg.label}
    >
      <I className={`${size === 'md' ? 'w-3 h-3' : 'w-2.5 h-2.5'} mr-1`} />
      {compact ? cfg.short : cfg.label}
    </Badge>
  );
};

/**
 * Inline short label e.g. "Brand Ambassador (Primary)" — use where a
 * full Badge element would be too heavy (titles, breadcrumbs, PDF-ish
 * plain-text lines). Null → "(Primary)" for safety.
 */
export const assignmentShortLabel = (type) => {
  const key = (type || 'primary').toLowerCase();
  return ASSIGNMENT_MAP[key]?.short || 'Primary';
};

export default AssignmentBadge;
